/**
 *
 * MessagesList
 *
 */

import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Empty } from 'antd';

// layout components
import Preloader from 'components/Preloader';
import ErrorBoundary from 'engine/ErrorBoundary';

// Communication components
import { Message } from 'components/Communications';

const MessagesList = React.forwardRef(
  (
    {
      loading,
      success,
      firstLoad,
      list,
      newMessage,
      reply,
      scrollToMsg,
      currentPage,
      // actions
      onReply,
      setReply,
      setScrollToMsg,
      // default props
      className,
    },
    ref,
  ) => {
    const childClassNames = classNames('messages-list-wrapper', className);

    const messagesEndRef = useRef(null);

    let timeOutListScroll = false;

    useEffect(() => { 
      return () => {
        // this will clear setTimeout
        timeOutListScroll && clearTimeout(timeOutListScroll);
        setScrollToMsg(false);
      };
    }, [])

    useEffect(() => {
      if (
        (!!firstLoad || !!newMessage || !!success || list?.length > 0) &&
        messagesEndRef?.current
      ) {
        if (!!scrollToMsg) {
          timeOutListScroll = setTimeout(() => {
            const domMsgElement = document.getElementById(
              `${scrollToMsg}-wrapper`
            );
            const domListElement = document.getElementById(
              `messages-list`,
            );
            domMsgElement && domListElement.scrollTo({
              left: 0,
              top: domMsgElement.offsetTop,
              behavior: 'smooth'
            });
          }, 200);
        } else {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, [firstLoad, newMessage, success, list]);

    const renderMessages = useMemo(() => {
      return (
        <>
          {list?.map(message => (
            <Message
              key={message?._id}
              message={message}
              onReply={onReply}
              setReply={setReply}
            />
          ))}
        </>
      );
    }, [list, list?.length, firstLoad, loading, onReply, setReply, currentPage]);
    
    return (
      <div className={childClassNames} ref={ref}>
        <ErrorBoundary>
          {loading && <Preloader className="messages-preload" />}
          <div id="messages-list" className={classNames('messages', reply && 'with-reply')}>
            {list?.length > 0 && renderMessages}
            {list?.length == 0 && !newMessage && (
              <Empty description="Aucune discussion" />
            )}
            {/* Show only for new sending message, without rerendering whole list */}
            {!!newMessage && !list?.find(el => el?._id == newMessage?._id) && (
              <Message
                key={newMessage?._id}
                message={newMessage}
                onReply={onReply}
                setReply={setReply}
              />
            )}
            <div ref={messagesEndRef} />
          </div>
        </ErrorBoundary>
      </div>
    );
  },
);

MessagesList.defaultProps = {
  loading: false,
  firstLoad: false,
  success: false,
  list: [],
};
MessagesList.propTypes = {};

export default MessagesList;
